import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Typography,
  Container,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Link,
  InputAdornment,
  IconButton
} from '@mui/material';
import { Visibility, VisibilityOff, ArrowBack } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser, loginWithEmail } = useAuth();
  const [form, setForm] = useState({
    email: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (currentUser) {
      navigate('/profile');
    }
  }, [currentUser, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const user = await loginWithEmail(form.email, form.password);
      if (!user.emailVerified) {
        setError('Lütfen e-posta adresinizi doğrulayın. Doğrulama maili gelen kutunuzda olabilir.');
        setLoading(false);
        return;
      }
      navigate('/profile');
    } catch (err: any) {
      setError(err.message || 'Giriş sırasında bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Giriş Yap - HatTravel</title>
        <meta name="description" content="HatTravel e-posta ile giriş sistemi." />
      </Helmet>
      <Box sx={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', minHeight: '100vh', display: 'flex', alignItems: 'center' }}>
        <Container maxWidth="sm">
          <Box sx={{ py: 4 }}>
            <Box sx={{ mb: 4 }}>
              <Button
                startIcon={<ArrowBack />}
                component={RouterLink}
                to="/"
                sx={{ mb: 2, color: '#667eea', '&:hover': { backgroundColor: 'rgba(102, 126, 234, 0.1)' } }}
              >
                Ana Sayfaya Dön
              </Button>
            </Box>
            <Card sx={{ background: 'rgba(255, 255, 255, 0.9)', backdropFilter: 'blur(10px)', borderRadius: 4, boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)', transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)', '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 25px 50px rgba(0, 0, 0, 0.15)' } }}>
              <CardContent sx={{ p: 5 }}>
                <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 200, textAlign: 'center', background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)', backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', mb: 2 }}>
                  Giriş Yap
                </Typography>
                <Typography variant="h6" color="#7f8c8d" sx={{ textAlign: 'center', mb: 4, fontWeight: 300 }}>
                  E-posta ve şifrenizle giriş yapın
                </Typography>
                {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }}>{error}</Alert>}
                <form onSubmit={handleLogin}>
                  <TextField
                    fullWidth
                    label="E-posta"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                    type="email"
                    sx={{ mb: 3, '& .MuiOutlinedInput-root': { borderRadius: 3, background: 'rgba(255, 255, 255, 0.8)', '&:hover': { background: 'rgba(255, 255, 255, 1)' } } }}
                  />
                  <TextField
                    fullWidth
                    label="Şifre"
                    name="password"
                    value={form.password}
                    onChange={handleChange}
                    required
                    type={showPassword ? 'text' : 'password'}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowPassword((show) => !show)} edge="end">
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                    sx={{ mb: 3, '& .MuiOutlinedInput-root': { borderRadius: 3, background: 'rgba(255, 255, 255, 0.8)', '&:hover': { background: 'rgba(255, 255, 255, 1)' } } }}
                  />
                  <Button
                    fullWidth
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : null}
                    sx={{ borderRadius: 3, backgroundColor: '#005BBD', '&:hover': { backgroundColor: '#004A9F' }, '&:disabled': { backgroundColor: '#ccc' } }}
                  >
                    {loading ? 'Giriş Yapılıyor...' : 'Giriş Yap'}
                  </Button>
                </form>
                <Box sx={{ mt: 4, textAlign: 'center' }}>
                  <Typography variant="body1" color="#7f8c8d" sx={{ fontWeight: 300 }}>
                    Hesabınız yok mu?{' '}
                    <Link component={RouterLink} to="/register" sx={{ color: '#667eea', textDecoration: 'none', fontWeight: 500, '&:hover': { textDecoration: 'underline' } }}>
                      Kayıt Ol
                    </Link>
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default LoginPage; 